import type { JSX, ReactNode } from 'react';
import { Group, Paper, Stack, Text, Title } from '@mantine/core';

/**
 * One block of a page: a heading, an optional line saying what it is for,
 * actions on the right, and the content. Every settings-like page in PST is
 * built from these, so they look and read the same.
 */
export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="sm">
        {/* Wraps on narrow screens: the actions move below instead of squeezing the text out of its box. */}
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={2} style={{ minWidth: 0, flex: '1 1 260px' }}>
            <Title order={5}>{title}</Title>
            {description && (
              <Text size="xs" c="dimmed">{description}</Text>
            )}
          </Stack>
          {actions && <Group gap="xs" wrap="wrap">{actions}</Group>}
        </Group>
        {children}
      </Stack>
    </Paper>
  );
}

/** Page heading with an optional subtitle and actions, above the sections. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
      <Stack gap={2}>
        <Title order={3}>{title}</Title>
        {description && <Text size="sm" c="dimmed">{description}</Text>}
      </Stack>
      {actions && <Group gap="xs">{actions}</Group>}
    </Group>
  );
}

/** Initials in a circle - enough to tell three or five people apart at a glance. */
export function initials(name: string): string {
  // Only words that begin with a letter: "(vorübergehend)" or "–" are not names.
  const parts = name.trim().split(/\s+/).filter((part) => /^\p{L}/u.test(part));
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}
